import React, { useState } from 'react';

import { SegmentedRadioGroup } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { LoadingDataReducerStateType } from 'client/reducers/loadingData';
import { getChartColor } from 'client/utils/color';
import {
    getAliceAppYAxisLabelProps,
    getChartStateLabel,
    getTickFormat,
    getTicks,
    labelFormatter,
    tickFormatter,
} from 'client/utils/monitoring';
import * as keyset from 'i18n/Monitoring';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

interface Props {
    data: ({ ts: number } & Record<string, number | null>)[];
    labels: string[];
    dataLoadingState: LoadingDataReducerStateType;
    onIntervalChange: (start: number, end: number) => void;
    from: number;
    to: number;
    yAxisLabel?: string;
    yAxisThreshold?: number;
    yAxisThresholdLabel?: string;
    disableStacking?: boolean;
}

const t = i18n(keyset);

const OPTIONS = [
    {
        value: '75',
        content: '75%',
    },
    {
        value: '95',
        content: '95%',
    },
    {
        value: '99',
        content: '99%',
    },
];

export const AliceAppMonitoringPageSmartHomeTiming: React.FC<Props> = ({ labels, ...restProps }) => {
    const [currentPerc, setCurrentPerc] = useState('75');

    return (
        <div>
            <div>
                <span>{t('label-percentile')}</span>
                <SegmentedRadioGroup
                    disabled={restProps.dataLoadingState === 'loading'}
                    value={currentPerc}
                    onUpdate={setCurrentPerc}
                    options={OPTIONS}
                />
            </div>
            <AliceAppMonitoringPageAreaChart labels={labels.filter((l) => l.endsWith(currentPerc))} {...restProps} />
        </div>
    );
};

export const AliceAppMonitoringPageAreaChart: React.FC<Props> = ({
    data,
    labels,
    onIntervalChange,
    dataLoadingState,
    from,
    to,
    yAxisLabel,
    yAxisThreshold,
    yAxisThresholdLabel,
    disableStacking,
}) => {
    const [start, setStart] = useState<number>();
    const [end, setEnd] = useState<number>();
    const [hiddenLabels, setHiddenLabels] = useState<string[]>([]);

    const hasValidRange = start !== undefined && end !== undefined;
    const format = getTickFormat(from, to);

    const initialFrom = data[0].ts;
    const initialTo = data[data.length - 1].ts;

    const initState = () => {
        setStart(undefined);
        setEnd(undefined);
    };

    const showChartStatus = ['loading', 'error'].includes(dataLoadingState);

    return (
        <ResponsiveContainer width="100%" height={300}>
            <AreaChart
                data={data}
                syncId="syncId"
                onMouseDown={(e) => e && setStart(Number(e.activeLabel))}
                onMouseMove={(e) => e && start !== undefined && setEnd(Number(e.activeLabel))}
                onMouseUp={() => {
                    if (hasValidRange) {
                        onIntervalChange(end!, start!);
                    }
                    initState();
                }}
                onMouseLeave={initState}
            >
                <XAxis
                    dataKey="ts"
                    tickFormatter={(tick) => tickFormatter(tick, format)}
                    minTickGap={30}
                    ticks={getTicks(
                        from,
                        to,
                        data.map(({ ts }) => ts),
                    )}
                />
                <YAxis label={yAxisLabel && getAliceAppYAxisLabelProps(yAxisLabel)} />
                <Legend
                    onClick={({ value, inactive }) => {
                        inactive
                            ? setHiddenLabels(hiddenLabels.filter((x) => x !== value))
                            : setHiddenLabels([...hiddenLabels, value]);
                    }}
                />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip isAnimationActive={false} labelFormatter={labelFormatter} />
                {hasValidRange && (
                    <ReferenceArea
                        x1={start}
                        x2={end}
                        strokeOpacity={0.8}
                        stroke="#3182bd"
                        strokeDasharray="3 3"
                        fill="#3182bd"
                        fillOpacity={0.3}
                    />
                )}
                {labels.map((label, i) => (
                    <Area
                        dataKey={label}
                        key={i}
                        stackId={disableStacking ? undefined : 'stackId'}
                        isAnimationActive={false}
                        stroke={getChartColor(label)}
                        strokeWidth={1}
                        strokeOpacity={0.7}
                        strokeLinejoin="round"
                        fill={getChartColor(label)}
                        hide={hiddenLabels.includes(label)}
                    />
                ))}
                {showChartStatus && (
                    <ReferenceArea
                        x1={initialFrom}
                        x2={initialTo}
                        strokeWidth={0}
                        fill="#ccc"
                        fillOpacity={0.2}
                        isFront
                        {...{ label: getChartStateLabel(dataLoadingState as 'loading' | 'error') }}
                    />
                )}
                {Boolean(yAxisThreshold) && (
                    <ReferenceArea
                        y1={yAxisThreshold}
                        strokeWidth={1}
                        strokeOpacity={0.1}
                        stroke="#f00"
                        fill="#f00"
                        fillOpacity={0.04}
                        isFront
                    />
                )}
                {Boolean(yAxisThreshold) && (
                    <ReferenceLine
                        y={yAxisThreshold}
                        strokeWidth={1}
                        stroke="#f00"
                        strokeDasharray="3 3"
                        isFront
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        label={{ value: yAxisThresholdLabel, position: 'right', fill: '#e8511a' } as any}
                    />
                )}
            </AreaChart>
        </ResponsiveContainer>
    );
};
